/**
 * Scroll Reveal — minimal IntersectionObserver-based reveal system.
 *
 * Markup contract:
 *   <element data-reveal>                       — default fade-up
 *   <element data-reveal="fade-up">             — fade + translateY(24px → 0)
 *   <element data-reveal="fade-in">             — opacity only
 *   <element data-reveal="scale-in">            — opacity + scale(0.94 → 1)
 *   <element data-reveal="fade-right">          — fade + translateX(-24px → 0)
 *   <element data-reveal-delay="120">           — milliseconds (added as transition-delay)
 *   <element data-reveal-group>                 — auto-stagger direct children with [data-reveal]
 *
 * On intersect, the `is-revealed` class is added and stays. We never re-hide.
 * Honours prefers-reduced-motion (CSS handles that — we still flip the class,
 * but the transition becomes near-instant via the global reduced-motion rule).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    // Old browser fallback: reveal everything immediately.
    var fallback = document.querySelectorAll('[data-reveal]');
    fallback.forEach(function (el) { el.classList.add('is-revealed'); });
    return;
  }

  var STAGGER_STEP_MS = 90;
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      el.classList.add('is-revealed');
      observer.unobserve(el);
    });
  }, {
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.12
  });

  function applyGroupStagger(group) {
    var children = group.querySelectorAll(':scope > [data-reveal]');
    children.forEach(function (child, index) {
      if (child.dataset.revealDelay) return; // respect explicit delays
      child.dataset.revealDelay = String(index * STAGGER_STEP_MS);
    });
  }

  function init(root) {
    var scope = root || document;

    // First, expand auto-stagger groups so children get delays before observing.
    var groups = scope.querySelectorAll('[data-reveal-group]');
    groups.forEach(applyGroupStagger);

    var targets = scope.querySelectorAll('[data-reveal]:not(.is-revealed)');
    targets.forEach(function (el) {
      var delay = el.dataset.revealDelay;
      if (delay) {
        el.style.transitionDelay = delay + 'ms';
      }
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Re-scan when Shopify theme editor injects a section.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Expose a manual entry point for dynamically-inserted content.
  window.MemoketScrollReveal = { observe: init };
})();
